import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const name = form.name.trim();
    const email = form.email.trim();
    const subject = form.subject.trim();
    const message = form.message.trim();
    if (!name) return toast.error("Please enter your name.");
    if (!EMAIL_RE.test(email)) return toast.error("Please enter a valid email address.");
    if (!subject) return toast.error("Please add a subject.");
    if (!message) return toast.error("Please write a message.");
    setSubmitting(true);
    try {
      await api.post("/contact", { name, email, subject, message, website });
      toast.success("Thanks — we'll get back to you shortly.");
      setDone(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "We couldn't send your message right now. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-tight py-12" data-testid="contact-page">
      <div className="font-mono text-xs tracking-widest text-[#FF5C00] mb-2">// CONTACT</div>
      <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-3">
        Talk to Interstate PC
      </h1>
      <p className="text-zinc-400 text-sm mb-8 max-w-2xl leading-relaxed">
        Business, product, or bulk-order inquiries? Send us a note and we&rsquo;ll get back to you
        as quickly as possible. Or email us directly at{" "}
        <a href="mailto:sales@interstate-pc.com" className="text-[#FF5C00] hover:underline">
          sales@interstate-pc.com
        </a>.
      </p>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <form onSubmit={onSubmit} className="space-y-4"
                data-testid="contact-form" noValidate>
            {/* Honeypot — hidden from users, filled by bots */}
            <input
              type="text" name="website" tabIndex={-1} autoComplete="off"
              value={website} onChange={(e) => setWebsite(e.target.value)}
              style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
              aria-hidden="true"
            />
            <Field label="Name" required>
              <input value={form.name} onChange={set("name")} maxLength={120}
                     required disabled={submitting} autoComplete="name"
                     data-testid="contact-name-input"
                     className={inputCls} />
            </Field>
            <Field label="Email" required>
              <input type="email" value={form.email} onChange={set("email")} maxLength={200}
                     required disabled={submitting} autoComplete="email"
                     data-testid="contact-email-input"
                     className={inputCls} />
            </Field>
            <Field label="Subject" required>
              <input value={form.subject} onChange={set("subject")} maxLength={200}
                     required disabled={submitting}
                     data-testid="contact-subject-input"
                     className={inputCls} />
            </Field>
            <Field label="Message" required>
              <textarea value={form.message} onChange={set("message")} maxLength={5000}
                        required disabled={submitting} rows={6}
                        data-testid="contact-message-input"
                        className={inputCls} />
            </Field>
            <button
              type="submit" disabled={submitting}
              data-testid="contact-submit-btn"
              className="bg-[#FF5C00] hover:bg-[#FF7A33] disabled:bg-zinc-700 disabled:cursor-not-allowed text-black font-mono text-xs tracking-widest uppercase font-bold px-5 py-3 rounded-sm transition-colors">
              {submitting ? "Sending..." : done ? "Sent" : "Send Message"}
            </button>
          </form>
        </div>

        <aside className="border border-zinc-800 bg-[#0a0a0c] rounded-sm p-6">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase mb-3">
            DIRECT
          </div>
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-4 h-4 text-[#FF5C00]" />
            <a href="mailto:sales@interstate-pc.com" className="text-sm text-white hover:text-[#FF5C00]">
              sales@interstate-pc.com
            </a>
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Business hours responses. Interstate PC is currently in pre-launch —
            online ordering is not yet available.
          </p>
        </aside>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#FF5C00] disabled:opacity-60";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase block mb-1.5">
        {label}{required && <span className="text-[#FF5C00]">*</span>}
      </span>
      {children}
    </label>
  );
}


export function PolicyPlaceholder({ title }) {
  return (
    <div className="container-tight py-16" data-testid={`policy-${title?.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="font-mono text-xs tracking-widest text-[#FF5C00] mb-2">// POLICY</div>
      <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-4">{title}</h1>
      <p className="text-zinc-400 text-sm leading-relaxed max-w-2xl">
        Placeholder — the final {title.toLowerCase()} will be published once Interstate PC
        finalizes supplier and carrier agreements. For questions in the meantime, email{" "}
        <a href="mailto:sales@interstate-pc.com" className="text-[#FF5C00] hover:underline">
          sales@interstate-pc.com
        </a>.
      </p>
    </div>
  );
}
